import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Rocket, ShieldCheck, Globe, Zap, 
  Settings, CreditCard, ChevronRight, Menu, 
  Check, Smartphone, Printer, CloudOff
} from 'lucide-react';

export default function LandingPage() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const PLANS = [
    { id: 'basic', price: '49€', features: [t('landing.feature_fiscal'), '1 Terminal', 'Standard Support'] },
    { id: 'pro', price: '89€', features: [t('landing.feature_fiscal'), '3 Terminals', '24/7 Support', 'CRM Integration'], priority: true },
    { id: 'enterprise', price: '199€', features: [t('landing.feature_fiscal'), 'Unlimited Terminals', 'API Access', 'Dedicated Manager'] }
  ];

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden selection:bg-emerald-200">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#1a2f38] flex items-center justify-center text-white shadow-lg">
              <Rocket className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold tracking-tight text-[#1a2f38]">FiscalPOS</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-[#1a2f38] transition-colors">{t('landing.features_title')}</a>
            <a href="#pricing" className="hover:text-[#1a2f38] transition-colors">{t('landing.pricing_title')}</a>
            <div className="flex gap-2 border-l pl-8 border-slate-200">
              {['de', 'en', 'sq'].map(lang => (
                <button 
                  key={lang} 
                  onClick={() => changeLanguage(lang)}
                  className={`uppercase w-8 h-8 flex items-center justify-center rounded-md text-xs font-bold transition-all
                    ${i18n.language === lang ? 'bg-[#1a2f38] text-white shadow-md' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                >
                  {lang}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/pos/login">
              <Button variant="ghost" className="text-slate-600 font-bold hover:bg-slate-100">Login</Button>
            </Link>
            <Link to="/admin/login">
              <Button className="bg-[#1a2f38] hover:bg-[#2a4048] font-bold shadow-md">Register</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 md:pt-48 md:pb-32 overflow-hidden bg-gradient-to-b from-[#1a2f38] to-[#0a151b] text-white">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] rounded-full bg-emerald-500 blur-[150px]" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-blue-500 blur-[150px]" />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8 text-center lg:text-left">
            <div className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-emerald-400 text-sm font-bold shadow-xl animate-bounce">
              🚀 Final Release v2.0
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold leading-[1.1] mb-6">
              {t('landing.hero_title')}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium">
              {t('landing.hero_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
              <Link to="/admin/login">
                <Button size="lg" className="h-14 px-10 text-lg font-bold bg-emerald-500 hover:bg-emerald-600 shadow-xl transition-all hover:scale-105 active:scale-95">
                  {t('landing.cta_start')}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="h-14 px-10 text-lg font-bold border-white/30 hover:bg-white/10 transition-all font-mono">
                Explore Demo
              </Button>
            </div>
          </div>
          
          <div className="relative group">
            <div className="absolute inset-0 bg-emerald-400/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4 shadow-2xl transform hover:rotate-2 transition-transform duration-500 overflow-hidden">
               <div className="flex items-center gap-2 mb-4 px-2">
                 <div className="w-3 h-3 rounded-full bg-red-400" />
                 <div className="w-3 h-3 rounded-full bg-amber-400" />
                 <div className="w-3 h-3 rounded-full bg-emerald-400" />
               </div>
               <img 
                 src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=1200" 
                 alt="FiscalPOS Terminal"
                 className="rounded-xl shadow-lg opacity-90 brightness-110"
               />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 px-4 max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#1a2f38] mb-4">{t('landing.features_title')}</h2>
          <div className="w-24 h-1.5 bg-emerald-500 mx-auto rounded-full" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { icon: ShieldCheck, title: t('landing.feature_fiscal'), desc: t('landing.feature_fiscal_desc'), color: 'bg-emerald-50 text-emerald-600' },
            { icon: CloudOff, title: t('landing.feature_offline'), desc: t('landing.feature_offline_desc'), color: 'bg-blue-50 text-blue-600' },
            { icon: Printer, title: t('landing.feature_hardware'), desc: t('landing.feature_hardware_desc'), color: 'bg-purple-50 text-purple-600' },
            { icon: Smartphone, title: 'Multi-Terminal', desc: 'Sync multiple devices in real-time within your location.', color: 'bg-amber-50 text-amber-600' },
            { icon: Globe, title: 'Multi-Location', desc: 'Manage dozens of stores from a single central manager.', color: 'bg-rose-50 text-rose-600' },
            { icon: Zap, title: 'Lightning Fast', desc: 'Optimized for speed. No lag, even during peak hours.', color: 'bg-orange-50 text-orange-600' }
          ].map((f, i) => (
            <Card key={i} className="group border-slate-100 hover:border-[#1a2f38]/20 transition-all hover:shadow-2xl hover:-translate-y-2 p-4">
              <CardContent className="p-6 space-y-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${f.color} shadow-sm group-hover:scale-110 transition-transform`}>
                  <f.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-[#1a2f38]">{f.title}</h3>
                <p className="text-slate-500 leading-relaxed font-medium">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-4 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none opacity-20">
           <div className="absolute top-1/2 left-[-10%] w-[40%] h-[40%] bg-blue-600 rounded-full blur-[150px]" />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20 space-y-4">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white">{t('landing.pricing_title')}</h2>
            <p className="text-slate-400 font-medium">No hidden fees. Cancel anytime.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch pt-4">
            {PLANS.map((plan, i) => (
              <div 
                key={i} 
                className={`relative p-8 rounded-3xl border transition-all hover:scale-105 active:scale-95 flex flex-col items-center text-center
                  ${plan.priority 
                    ? 'bg-gradient-to-br from-emerald-500 to-emerald-700 border-white/20 shadow-2xl scale-110' 
                    : 'bg-white/5 border-white/10 hover:bg-white/10 shadow-xl'}`}
              >
                {plan.priority && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-emerald-600 text-xs font-black uppercase px-6 py-1.5 rounded-full shadow-lg">
                    Most Popular
                  </div>
                ) }
                <h3 className="text-2xl font-black mb-2">{t(`landing.plan_${plan.id}`)}</h3>
                <div className="flex items-baseline gap-1 mb-8">
                  <span className="text-5xl font-black">{plan.price}</span>
                  <span className={plan.priority ? 'text-white/70' : 'text-slate-500'}>/mo</span>
                </div>
                
                <div className="space-y-4 mb-10 text-left w-full">
                  {plan.features.map((feat, j) => (
                    <div key={j} className="flex items-center gap-3 text-sm font-bold">
                      <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-emerald-400" />
                      </div>
                      <span className={plan.priority ? 'text-white' : 'text-slate-300'}>{feat}</span>
                    </div>
                  ))}
                </div>
                
                <Button className={`w-full h-12 rounded-xl font-bold text-base mt-auto
                  ${plan.priority 
                    ? 'bg-white text-emerald-700 hover:bg-slate-100 shadow-xl' 
                    : 'bg-[#1a2f38] hover:bg-[#2a4048] border border-white/10'}`}>
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-16 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
           <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#1a2f38] flex items-center justify-center text-white">
              <Rocket className="w-4 h-4" />
            </div>
            <span className="text-xl font-bold tracking-tight text-[#1a2f38]">FiscalPOS</span>
          </div>
          <div className="flex gap-8 text-sm font-semibold text-slate-500">
            <a href="#" className="hover:text-[#1a2f38]">Privacy</a>
            <a href="#" className="hover:text-[#1a2f38]">Terms</a>
            <a href="#" className="hover:text-[#1a2f38]">Support</a>
            <a href="#" className="hover:text-[#1a2f38]">Contact</a>
          </div>
          <p className="text-sm font-medium text-slate-400">© 2026 FiscalPOS — Built with AI for scale.</p>
        </div>
      </footer>
    </div>
  );
}
